// api/axiosInstance.ts
import axios, { AxiosInstance, AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { notify, isTechnicalMessage, markServerErrorShown } from '../../utils/notify';
import { emitUpgradeRequired } from '../../contexts/UpgradeModalContext';
import { clearAuthStorage } from '../../utils/authStorage';
import { requestStepUp } from './stepUp';
import { hasRefreshToken, refreshAccessToken, RefreshUnavailableError } from './tokenRefresh';
import {
  genericErrorText,
  localizeServerMessage,
  networkErrorText,
  sessionExpiredText,
  statusText,
} from '../../i18n/serverErrors';

/**
 * Per-request opt-out from the generic 403 toast, declared on axios's own config type so
 * a caller can set it like any other request option instead of casting past the type.
 *
 * Set it ONLY on a request whose caller already handles refusal itself - see the 403
 * branch of the response interceptor for why, and `getSlaPolicies` for the case that
 * prompted it.
 */
declare module 'axios' {
  export interface AxiosRequestConfig {
    quiet403?: boolean;
  }
}

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** Set once a request has been replayed after a step-up, so it can never loop. */
    _stepUpRetried?: boolean;
  }
}

declare global {
  interface Window {
    __sessionExpiredShown?: boolean;
    __lastCorrelationId?: string;
  }
}

// ── Expected failures ─────────────────────────────────────────────────────────
// A call may declare, per request, the HTTP statuses that are a normal ANSWER for
// it rather than a fault worth interrupting the user over. The interceptors below
// stay silent for exactly those statuses and for nothing else; the promise is
// still rejected, so the caller handles the outcome as it always did.
//
// Why this exists: the 403 branch toasts before the rejection reaches the caller,
// so a call whose own catch deliberately swallows a 403 could not stop the toast.
// The Insights screen reads the payroll period lock, whose gate is PayrollRun.View
// (Admin and Accountant only). An HR user is entitled to Insights and not to that
// read, so every load showed them a red "you don't have permission" for a lock
// state the screen already degrades to "unknown" on its own. Same for an
// organisation with no payroll module.
//
// This is opt-in per call and per status. Anything not listed still toasts, so a
// 403 that genuinely should surprise the user still does.
declare module 'axios' {
  export interface AxiosRequestConfig {
    /**
     * Statuses this call expects and handles itself, e.g. `[403]` on a read the
     * caller is allowed not to be permitted. Keep the list as narrow as the
     * reason for it: never use it to silence errors a user needs to see.
     */
    expectedErrorStatuses?: number[];
  }
}

/** True when the failing call declared this exact status as an expected answer. */
const isExpectedFailure = (error: AxiosError): boolean => {
  const status = error.response?.status;
  const expected = error.config?.expectedErrorStatuses;
  return status != null && Array.isArray(expected) && expected.includes(status);
};

// ── Correlation ID ────────────────────────────────────────────────────────────
// One id per outbound request, sent as X-Correlation-ID and propagated by every
// backend, so a single user action can be traced across all services in Seq.
// Backends echo it back; we stash the latest for support/debugging.
const genCorrelationId = (): string =>
  globalThis.crypto?.randomUUID?.() ??
  `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const captureCorrelationId = (response: AxiosResponse): AxiosResponse => {
  const cid = (response.headers?.['x-correlation-id'] ??
    response.headers?.['X-Correlation-ID']) as string | undefined;
  if (cid) window.__lastCorrelationId = cid;
  return response;
};

// ── Debounced server-error notification ──────────────────────────────────────
// Multiple concurrent API calls can all fail at once (e.g. dashboard loading
// 5 endpoints when the server is down). We show exactly one notification and
// suppress the rest until it has actually disappeared from screen.
//
// The cooldown used to be a flat 4s while the toast itself stayed visible for
// 6s (`duration: 6`). Any failing request that landed in that 4-6s gap — a
// staggered dashboard fetch, a slow retry — found the cooldown already lifted
// and popped a second, then a third, notification while the first one was
// still on screen: one real outage rendered as 2-3 stacked toasts. The
// cooldown must last at least as long as the toast is visible.
const SERVER_ERROR_TOAST_DURATION_S = 6;
let serverErrorCooldown = false;

const showServerError = (title: string, description?: string) => {
  if (serverErrorCooldown) return;
  serverErrorCooldown = true;

  // The one toast channel (utils/notify), with a fixed id so concurrent failures share one toast.
  notify.error(title, { id: 'server-error', description, duration: SERVER_ERROR_TOAST_DURATION_S * 1000 });
  markServerErrorShown();

  setTimeout(() => { serverErrorCooldown = false; }, SERVER_ERROR_TOAST_DURATION_S * 1000);
};

// ── API response message extraction ──────────────────────────────────────────
// Handles all known shapes from the various backends in this project:
//   .NET API:   { message, title }
//   Payroll:    { error, details, errorCode, correlationId }
//   NestJS:     { message, statusCode }
//   Plain text: "Some error string"
const extractApiParts = (data: any): { primary: string | null; secondary: string | null } => {
  if (!data) return { primary: null, secondary: null };

  if (typeof data === 'string' && data.length < 400) {
    return { primary: data, secondary: null };
  }

  const primary =
    (typeof data.message === 'string' ? data.message : null) ||
    (typeof data.title === 'string' ? data.title : null) ||
    (typeof data.error === 'string' ? data.error : null) ||
    null;

  const secondary =
    (typeof data.details === 'string' ? data.details : null) ||
    (typeof data.description === 'string' ? data.description : null) ||
    null;

  // Array messages (NestJS validation errors)
  if (!primary && Array.isArray(data.message)) {
    return { primary: data.message.join(' · '), secondary: null };
  }

  return { primary, secondary };
};

// ── Refusals that explain themselves ─────────────────────────────────────────
// Some 403s carry a real answer — which guard refused, and what to do about it.
// Others just restate the status code. Only the first kind is worth standing
// aside for, so the generic toast keeps firing for everything below.
//
// NestJS's default 403 body is `{"message":"Forbidden resource"}`, and .NET's
// ProblemDetails title is "Forbidden" — both would otherwise read as a specific
// message and silence a refusal that said nothing at all.
const GENERIC_REFUSAL_TEXT = new Set([
  'forbidden',
  'forbidden resource',
  'denied',
  'access denied',
  'access is denied',
  'unauthorized',
  'unauthorised',
  'not authorized',
  'not authorised',
  'no permission',
  'permission denied',
  'insufficient permission',
  'insufficient permissions',
  'you do not have permission',
  "you don't have permission",
  'you do not have permission to perform this action',
  "you don't have permission to perform this action",
  'you are not allowed to perform this action',
  'you are not allowed to access this resource',
]);

const isSpecificRefusal = (value: string | null): boolean => {
  const text = value?.trim().replace(/[.!]+$/, '').toLowerCase();
  if (!text) return false;
  return !GENERIC_REFUSAL_TEXT.has(text);
};

/** Does this 403 body name a reason a page could usefully show? */
const hasSpecificRefusal = (data: unknown): boolean => {
  const { primary, secondary } = extractApiParts(data);
  return isSpecificRefusal(primary) || isSpecificRefusal(secondary);
};

const getErrorParts = (
  error: AxiosError
): { title: string; description?: string } => {
  const status = error.response?.status;
  const data = error.response?.data as any;
  const rawParts = extractApiParts(data);
  // Backend text is translated only where it is exactly recognised; anything else keeps the
  // server's own words, because an untranslated accurate message beats a translated vague one.
  // See src/i18n/serverErrors.ts.
  // Technical text (exception names, SQL, provider/config details, gateway HTML) is dropped here,
  // so our own status line takes its place.
  const keep = (v: string | null) => (v && !isTechnicalMessage(v) ? v : null);
  const primary = keep(localizeServerMessage(rawParts.primary));
  const secondary = keep(localizeServerMessage(rawParts.secondary));

  // Network / no response
  if (!status) {
    const kind =
      error.code === 'ECONNABORTED' || error.message?.includes('timeout') ? 'timeout' : 'unreachable';
    return networkErrorText(kind);
  }

  // Statuses where the server's own message is preferred when present — it names the actual
  // problem, where ours can only name the status. 401/403/408/429 deliberately always use ours,
  // and so do 502/503/504: those come from the gateway, not the service, and the useful thing to
  // say is "try again in a moment".
  const PREFER_SERVER_MESSAGE = new Set([400, 404, 409, 422, 500]);

  const ownText = statusText(status);
  const desc =
    (PREFER_SERVER_MESSAGE.has(status) ? primary : null) ??
    ownText ??
    primary ??
    genericErrorText(status);

  // For 5xx: show the details line as a second line if present
  if (status >= 500 && secondary && secondary !== desc) {
    return { title: desc, description: secondary };
  }

  // For 400/422 with extra details
  if ((status === 400 || status === 422) && secondary && secondary !== desc) {
    return { title: desc, description: secondary };
  }

  return { title: desc };
};

// HRMS-11: overwrite AxiosError.message with the backend's user-facing text so that
// EVERY consumer - the shared parseApiError() helper AND the many call sites across
// modules that read raw `error.message` / `notify.error(error.message)` - surfaces the
// meaningful backend message instead of the generic "Request failed with status code N".
// Priority mirrors the ticket: details → message → error. No-op when the body carries
// none of those, leaving the original axios message as the last-resort fallback.
const normalizeErrorMessage = (error: AxiosError): void => {
  const data = error.response?.data as
    | { details?: unknown; message?: unknown; error?: unknown }
    | undefined;
  if (!data || typeof data !== 'object') return;
  const backendMsg = data.details ?? data.message ?? data.error;
  if (typeof backendMsg === 'string' && backendMsg.trim() && !isTechnicalMessage(backendMsg)) {
    error.message = backendMsg;
  }
};

// ── Single-flight refresh on 401 ──────────────────────────────────────────────
// The access JWT is short-lived. A 401 on a signed-in session first tries ONE refresh (shared by
// every request that failed meanwhile, on every instance, see tokenRefresh.ts) and replays the
// request with the new token. Only a refused refresh ends the session; an unreachable server does
// not. Client-portal sessions carry an ATS token with no refresh token and never enter this path.
type RetriableConfig = InternalAxiosRequestConfig & { _authRetried?: boolean };

const isRefreshCall = (url: string): boolean => /Login\/(refresh|logout)/i.test(url);

const canRefresh = (config: RetriableConfig | undefined, url: string): config is RetriableConfig =>
  !!config && !config._authRetried && hasRefreshToken() && !isRefreshCall(url) && !localStorage.getItem('clientUser');

const replayAfterRefresh = (
  client: AxiosInstance,
  config: RetriableConfig,
  original: AxiosError,
  onSessionEnded: () => Promise<never>
): Promise<any> => {
  config._authRetried = true;
  return refreshAccessToken().then(
    (token) => {
      config.headers.Authorization = `Bearer ${token}`;
      return client(config);
    },
    (refreshError) =>
      refreshError instanceof RefreshUnavailableError ? Promise.reject(original) : onSessionEnded()
  );
};

const endSessionFor401 = (error: AxiosError): Promise<never> => {
  if (!window.__sessionExpiredShown) {
    window.__sessionExpiredShown = true;
    notify.error(sessionExpiredText());
    clearAuthStorage();
    window.location.href = '/login';
  }
  return Promise.reject(error);
};

// ── Step-up (MFA) on 403 STEP_UP_REQUIRED ────────────────────────────────────
// Shared by every instance. Prompts once for the TOTP code, calls Mfa/step-up, replays the request
// ONCE (_stepUpRetried). Cancelled or failed: the original 403 propagates. Touches nothing else.
const isStepUp = (error: AxiosError): boolean =>
  error.response?.status === 403 &&
  (error.response?.data as any)?.code === 'STEP_UP_REQUIRED' &&
  !!error.config &&
  !error.config._stepUpRetried;

const replayAfterStepUp = (client: AxiosInstance, error: AxiosError): Promise<any> =>
  requestStepUp((code) =>
    // Plain post on the same client; _stepUpRetried stops this call from prompting again.
    client.post('Mfa/step-up', { code }, { _stepUpRetried: true, baseURL: import.meta.env.VITE_USER_BASE_URL })
  ).then((ok) => {
    if (!ok) return Promise.reject(error);
    return client({ ...(error.config as InternalAxiosRequestConfig), _stepUpRetried: true });
  });

// ── Axios instance factory ────────────────────────────────────────────────────

interface InstanceOptions {
  /**
   * Is this service an authority on OUR session? Default true.
   *
   * A 401 from the services that validate the same UM-issued JWT means the session really has
   * expired, and ending it is right. It is NOT right for a service that runs its own, separate
   * JWT configuration: its 401 says "this host does not accept your token", which is a statement
   * about that host, not about the user's session. Treating the two the same logs a working user
   * out of the entire app because one legacy side-service refused one background request.
   *
   * That is what the retired .NET recruiter host did; no current instance sets it.
   */
  ownsSession?: boolean;
}

const createAxiosInstance: any = (baseURL: string, options: InstanceOptions = {}): AxiosInstance => {
  const { ownsSession = true } = options;
  const instance = axios.create({
    baseURL,
    timeout: 600000, // 10 min - covers large file uploads
    maxContentLength: 50 * 1024 * 1024,
    maxBodyLength: 50 * 1024 * 1024,
    headers: { 'Content-Type': 'application/json' },
  });

  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
      const token = localStorage.getItem('token');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (config.headers) {
        config.headers['X-Correlation-ID'] = genCorrelationId();
      }
      return config;
    },
    (error: AxiosError) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response: AxiosResponse) => captureCorrelationId(response),
    (error: AxiosError) => {
      normalizeErrorMessage(error);
      if (isStepUp(error)) return replayAfterStepUp(instance, error);
      const status = error.response?.status;
      const requestUrl = error.config?.url || '';

      const isLoginRequest =
        requestUrl.includes('Login/login') || requestUrl.includes('/login') || requestUrl.includes('/auth/login');
      const isProfileUpdate =
        requestUrl.includes('update-profile') || requestUrl.includes('update-password');
      // ProbationReviewService.SubmitAsync throws UnauthorizedAccessException for "this
      // caller has no line authority over this employee and is not HR/Admin", which
      // GlobalExceptionMiddleware maps to 401, not 403 (Task B3 frontend contract). That
      // is a legitimate per-request refusal, not an expired session - auto-logging the
      // caller out for reviewing the wrong employee would be actively harmful.
      const isProbationReview = /lifecycle\/probation\/\d+\/review/i.test(requestUrl);
      // EmployeeDependentService.ListAsync throws UnauthorizedAccessException when its own
      // independent, email-based self-id resolution disagrees with the caller's actual
      // employee id (Critical 2, review-SPA-frontend.md) — GlobalExceptionMiddleware maps
      // that to 401, same as the probation-review case above. An ordinary employee opening
      // their OWN "My Dependents" tab must not be force-logged-out with a "session expired"
      // toast for a request that never touched anyone else's data and never expired.
      // Scoped to only the self/HR read route (GET .../EmployeeDependents/employee/{id}),
      // the one path an ordinary Employee actually calls — not the HR/Admin write routes.
      const isDependentsList = /EmployeeDependents\/employee\/\d+/i.test(requestUrl);

      // 401 - session expired (but don't redirect on login or profile update attempts)
      if (status === 401) {
        if (isLoginRequest || isProfileUpdate || isProbationReview || isDependentsList) {
          return Promise.reject(error);
        }

        // A service that does not share our auth authority cannot report on our session.
        if (!ownsSession) {
          return Promise.reject(error);
        }

        // Client-portal pages manage their own auth flow - don't auto-logout
        const isClientPortalPage = window.location.pathname.startsWith('/client/');
        if (isClientPortalPage) {
          return Promise.reject(error);
        }

        if (canRefresh(error.config as RetriableConfig | undefined, requestUrl)) {
          return replayAfterRefresh(instance, error.config as RetriableConfig, error, () => endSessionFor401(error));
        }

        return endSessionFor401(error);
      }

      // 402 - subscription / plan restriction
      if (status === 402) {
        const body = error.response?.data as any ?? {};
        emitUpgradeRequired({
          errorCode: body.errorCode ?? body.code ?? 'PlanUpgradeRequired',
          message: body.message ?? body.title ?? undefined,
          currentPlan: body.currentPlan ?? undefined,
          requiredPlan: body.requiredPlan ?? undefined,
          feature: body.feature ?? undefined,
          description: body.description ?? body.details ?? undefined,
        });
        return Promise.reject(error);
      }

      // 403 — permission denied.
      //
      // A refusal that explains itself must not be talked over. Payroll's
      // PayrollScopeGuard and User Management's ModuleDelegatedToGroup both
      // answer with a named reason and a remedy ("this module is run by the
      // group, ask X"), and firing a generic toast first buries it: the toast
      // wins the screen and the page-level handler's real message arrives second
      // or not at all.
      //
      // So the generic line is the fallback, not the default. It still fires
      // whenever the body says nothing specific — a 403 is never left silent —
      // and the shapes are read through extractApiParts, which already
      // normalizes .NET {message,title}, NestJS {message,statusCode} and payroll
      // {error,details,errorCode}.
      if (status === 403) {
        // Opt-out for BEST-EFFORT reads: a request that already degrades gracefully on
        // refusal must not also shout at the user. The approvals queue reads the SLA
        // policy list to decorate its ageing tiles and falls back to an empty list when
        // it cannot; that read is admin-only, so every HR approver opening their own
        // queue was told "You don't have permission to perform this action" about a
        // screen that had in fact loaded correctly and that they had every right to use.
        // Set `quiet403` only where the caller handles the refusal itself.
        if (error.config?.quiet403) {
          return Promise.reject(error);
        }
        if (!hasSpecificRefusal(error.response?.data)) {
          // Fixed `id`: a page firing several 403s at once (e.g. a dashboard
          // loading multiple restricted widgets in parallel) previously
          // stacked one identical toast per request. notify replaces an
          // already-visible toast sharing this id instead of adding a new
          // one, so concurrent 403s collapse to a single toast.
          notify.error(statusText(403) ?? "You don't have permission to perform this action.", { id: 'permission-denied' });
          // The real reason is on screen: the caller's own "Failed to fetch data" that follows
          // would only stack a vaguer second toast on top of it.
          markServerErrorShown();
        }
        return Promise.reject(error);
      }

      // 5xx server errors - rich notification with title + details
      if (status && status >= 500) {
        if (!isExpectedFailure(error)) {
          const { title, description } = getErrorParts(error);
          showServerError(title, description);
        }
        return Promise.reject(error);
      }

      // Network errors (no response - server down, DNS failure, etc.)
      if (!error.response && !status) {
        const { title, description } = getErrorParts(error);
        showServerError(title, description);
        return Promise.reject(error);
      }

      // 4xx client errors - pass through; callers handle these individually.
      return Promise.reject(error);
    }
  );

  return instance;
};

const coreApi = createAxiosInstance(import.meta.env.VITE_BASE_URL);
export const authApi = createAxiosInstance(import.meta.env.VITE_AUTH_BASE_URL);
export const employeeApi = createAxiosInstance(import.meta.env.VITE_EMPLOYEE_BASE_URL || import.meta.env.VITE_AUTH_BASE_URL);
export const payrollApi = createAxiosInstance(import.meta.env.VITE_PAYROLL_BASE_URL);
export const userApi = createAxiosInstance(import.meta.env.VITE_USER_BASE_URL);
// The legacy first-generation .NET recruiter host (Teams integration + CandidateRating; see
// docs/17-INTEGRATIONS-CONSOLIDATION.md, which is retiring it). It runs its own JWT validation
// rather than UM's, and rejects the token every other service here accepts — so its 401s are
// routine for us and must never end the session. `getCandidateRatingByIds` fires on every
// candidate detail open; without `ownsSession: false` that background read force-logged the
// user out of the whole app.
export const Api = createAxiosInstance(import.meta.env.VITE_BASIC_BASE_URL);
// Nova (AI agent service). Non-streaming calls only - the conversation itself
// streams over SSE, which does not go through axios. See docs/46-NOVA-BUILD-SPEC.md.
export const novaApi = createAxiosInstance(import.meta.env.VITE_NOVA_BASE_URL);

// ── NestJS API ────────────────────────────────────────────────────────────────
const nestBaseUrl = import.meta.env.VITE_BASIC_BASE_URL || 'http://localhost:9010/api/v1';
const nestInstance = axios.create({
  baseURL: nestBaseUrl,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' },
});
nestInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.headers) {
      config.headers['X-Correlation-ID'] = genCorrelationId();
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);
nestInstance.interceptors.response.use(
  (response: AxiosResponse) => captureCorrelationId(response),
  (error: AxiosError) => {
    normalizeErrorMessage(error);
    if (isStepUp(error)) return replayAfterStepUp(nestInstance, error);
    const status = error.response?.status;
    // Same refresh-and-replay as the other instances; a refused refresh just surfaces the 401
    // (this instance never ended the session before and still does not).
    if (status === 401 && canRefresh(error.config as RetriableConfig | undefined, error.config?.url || '')) {
      return replayAfterRefresh(nestInstance, error.config as RetriableConfig, error, () => Promise.reject(error));
    }
    if (status === 402) {
      const body = error.response?.data as any ?? {};
      emitUpgradeRequired({
        errorCode: body.errorCode ?? body.code ?? 'PlanUpgradeRequired',
        message: body.message ?? body.title ?? undefined,
        currentPlan: body.currentPlan ?? undefined,
        requiredPlan: body.requiredPlan ?? undefined,
        feature: body.feature ?? undefined,
        description: body.description ?? body.details ?? undefined,
      });
    }
    if (((status && status >= 500) || (!error.response && !status)) && !isExpectedFailure(error)) {
      const { title, description } = getErrorParts(error);
      showServerError(title, description);
    }
    return Promise.reject(error);
  },
);
export const NestApi = nestInstance;

export default coreApi;
